import type { CsatSurveyTemplateSummary } from "@redotech/merchant-sdk/rpc/schema/support/csat";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { useCallback, useMemo, useState } from "react";

/** Short enough to read in a toast; the fix belongs next to the control. */
export const MISSING_SURVEY_LINK_MESSAGE =
  "a selected template has no survey link";

/** How to add it, shown inline where the broken template is chosen. */
export const MISSING_SURVEY_LINK_FIX =
  "In the email builder, set a button's link type to Dynamic variable and choose the survey link, or insert the survey link variable as text.";

const DEFAULT_TEMPLATE_LABEL = "Default template";

export interface CsatSurveyTemplateCatalog {
  templates: CsatSurveyTemplateSummary[];
  /** False while loading or after a failed load, when `templates` is empty for
   *  reasons other than the team having none. */
  loaded: boolean;
  /** Templates that render a survey nobody can answer, so saving one is refused. */
  missingLinkIds: ReadonlySet<string>;
  /**
   * Flags the broken ones in the list itself, so a merchant sees it before
   * selecting rather than after their surveys stop using the template.
   */
  optionLabel: (templateId: string) => string;
  label: (templateId: string | undefined) => string;
  /** Pulls a newly created template into the picker so it shows a name, not an id. */
  reload: () => void;
}

/**
 * The team's CSAT survey templates, shared by the Templates and A/B panels and
 * by the save path. Only CSAT-context templates can print the survey link;
 * anything else silently falls back to the default layout at send time.
 */
export function useCsatSurveyTemplates(): CsatSurveyTemplateCatalog {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [listVersion, setListVersion] = useState(0);
  /* Support-scoped: picking the survey email must not require marketing access. */
  const templatesLoad = useLoad(
    async (signal) => await rpcClient.getCsatSurveyTemplates({}, { signal }),
    [rpcClient, listVersion],
  );
  const templates = useMemo(
    () => templatesLoad.value ?? [],
    [templatesLoad.value],
  );
  const loaded = templatesLoad.value !== undefined;
  const reload = useCallback(
    () => setListVersion((version) => version + 1),
    [],
  );

  /* Stable identity: consumers feed this into `useMemo`/`usePageActions` deps,
     and a fresh object each render makes those thrash. */
  return useMemo(() => {
    const namesById = new Map(
      templates.map((template) => [template.id, template.name]),
    );
    const missingLinkIds = new Set(
      templates
        .filter((template) => !template.hasSurveyLink)
        .map((template) => template.id),
    );
    return {
      templates,
      loaded,
      missingLinkIds,
      optionLabel: (templateId: string) =>
        `${namesById.get(templateId) ?? templateId}${
          missingLinkIds.has(templateId) ? " — missing survey link" : ""
        }`,
      label: (templateId: string | undefined) =>
        templateId === undefined
          ? DEFAULT_TEMPLATE_LABEL
          : (namesById.get(templateId) ?? templateId),
      reload,
    };
  }, [templates, loaded, reload]);
}
